// ============================================
// DataContext: Centralized data provider for StageFlow
// Loads all data from Supabase on mount, provides
// arrays + mutation functions to all pages.
// ============================================

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import * as api from './api';
import type {
  Project, Deliverable, CalendarEvent, Calendar, Venue, Customer,
  ProposalEvent, SocialMediaPost, Volunteer, VolunteerOpportunity, MerchItem,
  TeamMember, ProjectTemplate, HelpArticle,
  DepartmentRecord, LocationRecord, AuditLogEntry, AgendaItem,
} from './mockData';
import { DEFAULT_DEPARTMENTS, DEFAULT_LOCATIONS, withDeliverableOwner } from './mockData';

// ============================================
// Types
// ============================================

export interface SocialMediaSubmission {
  id: string;
  ideaType: 'existing' | 'new';
  calendarItemId?: string;
  categories: string[];
  title: string;
  caption: string;
  fileNames: string[];
  createdAt: string;
}

interface DataState {
  projects: Project[];
  deliverables: Deliverable[];
  events: CalendarEvent[];
  calendars: Calendar[];
  venues: Venue[];
  customers: Customer[];
  proposals: ProposalEvent[];
  socialPosts: SocialMediaPost[];
  volunteers: Volunteer[];
  opportunities: VolunteerOpportunity[];
  merch: MerchItem[];
  socialSubmissions: SocialMediaSubmission[];
  teamMembers: TeamMember[];
  templates: ProjectTemplate[];
  helpArticles: HelpArticle[];
  departments: DepartmentRecord[];
  locations: LocationRecord[];
  auditLog: AuditLogEntry[];
  agendaItems: AgendaItem[];
  fileCounts: Record<string, number>;
}

interface DataContextType extends DataState {
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;

  // Mutation functions — create/update/delete per collection
  createProject: (data: Partial<Project>) => Promise<Project>;
  updateProject: (id: string, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;

  createDeliverable: (data: Partial<Deliverable>) => Promise<Deliverable>;
  updateDeliverable: (id: string, data: Partial<Deliverable>) => Promise<void>;
  deleteDeliverable: (id: string) => Promise<void>;

  createEvent: (data: Partial<CalendarEvent>) => Promise<CalendarEvent>;
  updateEvent: (id: string, data: Partial<CalendarEvent>) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;

  createVenue: (data: Partial<Venue>) => Promise<Venue>;
  updateVenue: (id: string, data: Partial<Venue>) => Promise<void>;
  deleteVenue: (id: string) => Promise<void>;

  createCustomer: (data: Partial<Customer>) => Promise<Customer>;
  updateCustomer: (id: string, data: Partial<Customer>) => Promise<void>;
  deleteCustomer: (id: string) => Promise<void>;

  createProposal: (data: Partial<ProposalEvent>) => Promise<ProposalEvent>;
  updateProposal: (id: string, data: Partial<ProposalEvent>) => Promise<void>;
  deleteProposal: (id: string) => Promise<void>;

  createSocialPost: (data: Partial<SocialMediaPost>) => Promise<SocialMediaPost>;
  updateSocialPost: (id: string, data: Partial<SocialMediaPost>) => Promise<void>;
  deleteSocialPost: (id: string) => Promise<void>;

  createVolunteer: (data: Partial<Volunteer>) => Promise<Volunteer>;
  updateVolunteer: (id: string, data: Partial<Volunteer>) => Promise<void>;
  deleteVolunteer: (id: string) => Promise<void>;

  createOpportunity: (data: Partial<VolunteerOpportunity>) => Promise<VolunteerOpportunity>;
  updateOpportunity: (id: string, data: Partial<VolunteerOpportunity>) => Promise<void>;
  deleteOpportunity: (id: string) => Promise<void>;

  createMerch: (data: Partial<MerchItem>) => Promise<MerchItem>;
  updateMerch: (id: string, data: Partial<MerchItem>) => Promise<void>;
  deleteMerch: (id: string) => Promise<void>;

  createTeamMember: (data: Partial<TeamMember>) => Promise<TeamMember>;
  updateTeamMember: (id: string, data: Partial<TeamMember>) => Promise<void>;
  deleteTeamMember: (id: string) => Promise<void>;

  createTemplate: (data: Partial<ProjectTemplate>) => Promise<ProjectTemplate>;
  updateTemplate: (id: string, data: Partial<ProjectTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;

  createHelpArticle: (data: Partial<HelpArticle>) => Promise<HelpArticle>;
  updateHelpArticle: (id: string, data: Partial<HelpArticle>) => Promise<void>;
  deleteHelpArticle: (id: string) => Promise<void>;

  createSocialSubmission: (data: Partial<SocialMediaSubmission>) => Promise<SocialMediaSubmission>;

  createDepartment: (data: Partial<DepartmentRecord>) => Promise<DepartmentRecord>;
  updateDepartment: (id: string, data: Partial<DepartmentRecord>) => Promise<void>;
  deleteDepartment: (id: string) => Promise<void>;

  createLocation: (data: Partial<LocationRecord>) => Promise<LocationRecord>;
  updateLocation: (id: string, data: Partial<LocationRecord>) => Promise<void>;
  deleteLocation: (id: string) => Promise<void>;

  createAuditEntry: (data: Partial<AuditLogEntry>) => Promise<AuditLogEntry>;
  refreshAuditLog: () => Promise<void>;

  createAgendaItem: (data: Partial<AgendaItem>) => Promise<AgendaItem>;
  updateAgendaItem: (id: string, data: Partial<AgendaItem>) => Promise<void>;
  deleteAgendaItem: (id: string) => Promise<void>;
}

const DataContext = createContext<DataContextType | null>(null);

// ============================================
// Provider
// ============================================

export function DataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<DataState>({
    projects: [],
    deliverables: [],
    events: [],
    calendars: [],
    venues: [],
    customers: [],
    proposals: [],
    socialPosts: [],
    volunteers: [],
    opportunities: [],
    merch: [],
    socialSubmissions: [],
    teamMembers: [],
    templates: [],
    helpArticles: [],
    departments: [],
    locations: [],
    auditLog: [],
    agendaItems: [],
    fileCounts: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    try {
      setError(null);
      // Fetch main data and file counts in parallel
      const [rawResult, fileCountsResult] = await Promise.allSettled([
        api.fetchAll(),
        api.fetchFileCounts(),
      ]);
      const raw = rawResult.status === 'fulfilled' ? rawResult.value : {};
      const counts = fileCountsResult.status === 'fulfilled' ? fileCountsResult.value : {};
      if (rawResult.status === 'rejected') throw rawResult.reason;
      const rawDepartments: DepartmentRecord[] = raw['departments'] || [];
      const rawLocations: LocationRecord[] = raw['locations'] || [];

      // First-run seed for admin-managed lookup tables. `/api/all` skips
      // audit-log for size; that list is fetched on-demand from the Audit
      // Log page.
      let departments = rawDepartments;
      if (rawDepartments.length === 0) {
        const now = new Date().toISOString();
        const seeded = await Promise.all(
          DEFAULT_DEPARTMENTS.map(d =>
            api.createItem<DepartmentRecord>('departments', {
              ...d,
              createdAt: now,
              updatedAt: now,
            } as Partial<DepartmentRecord>),
          ),
        );
        departments = seeded;
      }

      let locations = rawLocations;
      if (rawLocations.length === 0) {
        const now = new Date().toISOString();
        const seeded = await Promise.all(
          DEFAULT_LOCATIONS.map(l =>
            api.createItem<LocationRecord>('locations', {
              ...l,
              createdAt: now,
              updatedAt: now,
            } as Partial<LocationRecord>),
          ),
        );
        locations = seeded;
      }

      setData({
        projects: raw.projects || [],
        deliverables: raw.deliverables || [],
        events: raw.events || [],
        calendars: raw.calendars || [],
        venues: raw.venues || [],
        customers: raw.customers || [],
        proposals: raw.proposals || [],
        socialPosts: raw['social-posts'] || [],
        volunteers: raw.volunteers || [],
        opportunities: raw.opportunities || [],
        merch: raw.merch || [],
        socialSubmissions: raw['social-submissions'] || [],
        teamMembers: raw['team-members'] || [],
        templates: raw['project-templates'] || [],
        helpArticles: raw['help-articles'] || [],
        departments,
        locations,
        auditLog: [],
        agendaItems: raw['agenda-items'] || [],
        fileCounts: counts,
      });
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    }
  }, []);

  // Initial load: seed then fetch
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // Seed is idempotent - safe to call every time
        await api.seed();
        if (!cancelled) await loadAll();
      } catch (err: any) {
        console.error('Initialization error:', err);
        if (!cancelled) setError(err.message || 'Initialization failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [loadAll]);

  // ============================================
  // Generic mutation factory
  // ============================================
  function makeCreate<T extends { id?: string }>(
    collection: string,
    stateKey: keyof DataState,
  ) {
    return async (item: Partial<T>): Promise<T> => {
      const created = await api.createItem<T>(collection, item);
      setData(prev => ({ ...prev, [stateKey]: [...prev[stateKey], created] }));
      return created;
    };
  }

  function makeUpdate<T extends { id: string }>(
    collection: string,
    stateKey: keyof DataState,
  ) {
    return async (id: string, item: Partial<T>): Promise<void> => {
      const updated = await api.updateItem<T>(collection, id, item);
      setData(prev => ({
        ...prev,
        [stateKey]: (prev[stateKey] as any[]).map((x: any) => x.id === id ? updated : x),
      }));
    };
  }

  function makeDelete(collection: string, stateKey: keyof DataState) {
    return async (id: string): Promise<void> => {
      await api.deleteItem(collection, id);
      setData(prev => ({
        ...prev,
        [stateKey]: (prev[stateKey] as any[]).filter((x: any) => x.id !== id),
      }));
    };
  }

  const ctx: DataContextType = {
    ...data,
    loading,
    error,
    refresh: loadAll,

    createProject: makeCreate<Project>('projects', 'projects'),
    updateProject: makeUpdate<Project>('projects', 'projects'),
    deleteProject: makeDelete('projects', 'projects'),

    createDeliverable: async (item: Partial<Deliverable>) => {
      const created = await api.createItem<Deliverable>('deliverables', withDeliverableOwner(item));
      setData(prev => ({ ...prev, deliverables: [...prev.deliverables, created] }));
      return created;
    },
    updateDeliverable: makeUpdate<Deliverable>('deliverables', 'deliverables'),
    deleteDeliverable: makeDelete('deliverables', 'deliverables'),

    createEvent: makeCreate<CalendarEvent>('events', 'events'),
    updateEvent: makeUpdate<CalendarEvent>('events', 'events'),
    deleteEvent: makeDelete('events', 'events'),

    createVenue: makeCreate<Venue>('venues', 'venues'),
    updateVenue: makeUpdate<Venue>('venues', 'venues'),
    deleteVenue: makeDelete('venues', 'venues'),

    createCustomer: makeCreate<Customer>('customers', 'customers'),
    updateCustomer: makeUpdate<Customer>('customers', 'customers'),
    deleteCustomer: makeDelete('customers', 'customers'),

    createProposal: makeCreate<ProposalEvent>('proposals', 'proposals'),
    updateProposal: makeUpdate<ProposalEvent>('proposals', 'proposals'),
    deleteProposal: makeDelete('proposals', 'proposals'),

    createSocialPost: makeCreate<SocialMediaPost>('social-posts', 'socialPosts'),
    updateSocialPost: makeUpdate<SocialMediaPost>('social-posts', 'socialPosts'),
    deleteSocialPost: makeDelete('social-posts', 'socialPosts'),

    createVolunteer: makeCreate<Volunteer>('volunteers', 'volunteers'),
    updateVolunteer: makeUpdate<Volunteer>('volunteers', 'volunteers'),
    deleteVolunteer: makeDelete('volunteers', 'volunteers'),

    createOpportunity: makeCreate<VolunteerOpportunity>('opportunities', 'opportunities'),
    updateOpportunity: makeUpdate<VolunteerOpportunity>('opportunities', 'opportunities'),
    deleteOpportunity: makeDelete('opportunities', 'opportunities'),

    createMerch: makeCreate<MerchItem>('merch', 'merch'),
    updateMerch: makeUpdate<MerchItem>('merch', 'merch'),
    deleteMerch: makeDelete('merch', 'merch'),

    createTeamMember: makeCreate<TeamMember>('team-members', 'teamMembers'),
    updateTeamMember: makeUpdate<TeamMember>('team-members', 'teamMembers'),
    deleteTeamMember: makeDelete('team-members', 'teamMembers'),

    createTemplate: makeCreate<ProjectTemplate>('project-templates', 'templates'),
    updateTemplate: makeUpdate<ProjectTemplate>('project-templates', 'templates'),
    deleteTemplate: makeDelete('project-templates', 'templates'),

    createHelpArticle: makeCreate<HelpArticle>('help-articles', 'helpArticles'),
    updateHelpArticle: makeUpdate<HelpArticle>('help-articles', 'helpArticles'),
    deleteHelpArticle: makeDelete('help-articles', 'helpArticles'),

    createSocialSubmission: makeCreate<SocialMediaSubmission>('social-submissions', 'socialSubmissions'),

    createDepartment: makeCreate<DepartmentRecord>('departments', 'departments'),
    updateDepartment: makeUpdate<DepartmentRecord>('departments', 'departments'),
    deleteDepartment: makeDelete('departments', 'departments'),

    createLocation: makeCreate<LocationRecord>('locations', 'locations'),
    updateLocation: makeUpdate<LocationRecord>('locations', 'locations'),
    deleteLocation: makeDelete('locations', 'locations'),

    createAgendaItem: makeCreate<AgendaItem>('agenda-items', 'agendaItems'),
    updateAgendaItem: makeUpdate<AgendaItem>('agenda-items', 'agendaItems'),
    deleteAgendaItem: makeDelete('agenda-items', 'agendaItems'),

    createAuditEntry: async (item: Partial<AuditLogEntry>) => {
      const created = await api.createItem<AuditLogEntry>('audit-log', item);
      setData(prev => ({ ...prev, auditLog: [created, ...prev.auditLog] }));
      return created;
    },
    refreshAuditLog: async () => {
      const items = await api.fetchCollection<AuditLogEntry>('audit-log');
      setData(prev => ({
        ...prev,
        auditLog: [...items].sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || '')),
      }));
    },
  };

  return <DataContext.Provider value={ctx}>{children}</DataContext.Provider>;
}

// ============================================
// Hook
// ============================================
export function useData(): DataContextType {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within a DataProvider');
  return ctx;
}